import * as PIXI from 'pixi.js-legacy';
import deepEqual from 'fast-deep-equal';
import emitter from '../emitter';
import offset from '../util/offset';
import productsService from '../services/products';
import constants from '../settings/constants';
import { DROP_ITEM, GRAB_ITEM, SCENE_REDRAWN } from '../settings/events';
import * as supportedEvents from '../util/supportedEvents';
import tacHelpers from '../state/tac/tacHelpers';
import * as actions from '../state/tac/tacActions.js';
import store from '../state';
import getComponent from './util/getComponent';
import ItemContainer from './ItemContainer';
import proppingService from '../services/propping';
import Rect from './Rect';
import DynamicRoom from './DynamicRoom';
import FixedRoom from './FixedRoom';
import {
  actionSetWallResizerInactive,
  actionSetProppingVisibility,
} from '../state/scene';
import { isFixedRoom } from '../util/room';
import MeasurementLayer from './MeasurementLayer';
import storage from '../services/history/storage';
import { actionShowSceneErrors, actionHideSceneErrors } from '../state/popups';
import { showTacToast } from '../services/toastMaster';
import { thunkRemoveItem, thunkUpdateItem } from '../state/tac/tacThunks';
import {
  selectSceneMargins,
  selectSceneRect,
} from '../state/scene/sceneSelectors';
import { applicationSettings } from '../settings/application';

window.oncontextmenu = () => {
  emitter.emit(DROP_ITEM, {
    abort: true,
  });
};

export default class SceneBase extends PIXI.Application {
  constructor(args) {
    PIXI.settings.ROUND_PIXELS = true;
    // capping SPRITE_MAX_TEXTURES fixes a webgl crash in firefox on linux
    PIXI.settings.SPRITE_MAX_TEXTURES = Math.min(
      PIXI.settings.SPRITE_MAX_TEXTURES,
      16
    );
    PIXI.settings.FAIL_IF_MAJOR_PERFORMANCE_CAVEAT = false;
    super({
      autoStart: false,
      autoResize: true,
      antialias: true,
      resolution: Math.max(window.devicePixelRatio, 1.5),
      transparent: true,
      height: args.viewRect.height,
      width: args.viewRect.width,
    });
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.update = this.update.bind(this);
    this.resizeScene = this.resizeScene.bind(this);
    this.getItemBounds = this.getItemBounds.bind(this);
    this.getSprite = this.getSprite.bind(this);
    this.adaptProppings = this.adaptProppings.bind(this);
    this.activate = this.activate.bind(this);
    this.renderer.plugins.interaction.autoPreventDefault = false;
    this.renderer.view.style['touch-action'] = 'auto';
    this.stage.interactive = true;
    const { userAgent } = store.getState();
    this.room = isFixedRoom()
      ? new FixedRoom(this.view, this.render.bind(this), this.stage)
      : new DynamicRoom();
    this.stage.addChild(this.room);
    this.rectContainer = this.rectContainer || new PIXI.Container();
    this.stage.addChild(this.rectContainer);
    this.itemContainer = new ItemContainer({
      onDragStart: this.onDragStart,
      app: this,
      isMobile: userAgent.isMobile,
    });
    this.stage.addChild(this.itemContainer);
    this.measurementLayer = new MeasurementLayer({
      autoStart: false,
      autoResize: true,
      resolution: Math.max(window.devicePixelRatio, 1.5),
      transparent: true,
      height: args.viewRect.height,
      width: args.viewRect.width,
      isMobile: userAgent.isMobile,
      scene: this,
    });
    this.hintContainer = new PIXI.Container();
    this.stage.addChild(this.hintContainer);
    this.hasRendered = false;
    this.resizeScene(args);
    // TODO check that this setup works correctly on computers with touch
    // screen AND mouse
    this.stage
      .on(supportedEvents.POINTER_MOVE, this.onDragMove)
      .on(supportedEvents.POINTER_UP, this.onDragEnd)
      .on(supportedEvents.POINTER_UP_OUTSIDE, this.onDragEnd);

    this.activate(args);
  }

  activate(args) {
    if (!applicationSettings.isProd) {
      window.PIXI = this;
    }
    this.args = args;
    this.unsubscribe = store.subscribe(this.update);
    this.update();

    emitter.on(DROP_ITEM, this.onDragEnd);
    emitter.on(
      GRAB_ITEM,
      (product, mousePosition, mode, eventType, domSize) => {
        if (this.dragging) {
          return;
        }

        const canvasOffset = offset(this.view);

        mousePosition.x -= canvasOffset.left;
        mousePosition.y -= canvasOffset.top;

        const item = {
          ...product,
          x:
            (mousePosition.x - (this.room.x + this.itemContainer.x)) /
              this.room.ratio -
            product.width / 2,
          y:
            this.room.model.height -
            (mousePosition.y - (this.room.y + this.itemContainer.y)) /
              this.room.ratio -
            product.height / 2,
          z: productsService.getInitialZPos(product),
        };

        // Set default propping
        const singleProppingBounds = proppingService.pickPropping({
          tac: this.tac,
          item,
        });
        if (singleProppingBounds) {
          item.propping = singleProppingBounds.id;
        }

        this.itemArgs = {
          onDragStart: this.onDragStart,
          ratio: this.room.ratio,
          parentItem: this.room.model,
          options: this.options,
          app: this,
        };

        const sprite = getComponent({ ...this.itemArgs, item });

        sprite.dummy = true;

        this.onDragStart({
          mode: productsService.getDragMode(item),
          mouse: mousePosition,
          offset: {
            x: sprite.width / 2 + this.itemContainer.x,
            y: sprite.height / 2 + this.itemContainer.y,
          },
          sprite: sprite,
          domSize: domSize,
        });
      }
    );
  }

  deactivate() {
    emitter.off(DROP_ITEM);
    emitter.off(GRAB_ITEM);
    this.args = {};
    this.unsubscribe();
  }

  /**
   * Creates and returns a base 64 string based on a texture created from the scene.
   * @returns {string}
   */
  createImageStringFromTexture(
    startingX,
    startingY,
    floor,
    height,
    resolution
  ) {
    const renderTexture = this.renderer.generateTexture(
      this.stage,
      PIXI.SCALE_MODES.LINEAR,
      resolution,
      new PIXI.Rectangle(startingX, startingY, floor.width, height)
    );
    const base64Image = this.renderer.extract.base64(renderTexture);
    renderTexture.destroy(true);
    return base64Image;
  }

  /**
   * Base 64 encoding creates 4 characters for each 3 bytes in a string.
   * https://sv.wikipedia.org/wiki/Base64
   * It also pads the string with (equal signs) to bring it to a certain block size
   * but it is omited here because we only need an approximate number.
   * @param {string} imageBase64String
   * @returns {number}
   */
  getFileSizeOfBase64Image(imageBase64String) {
    return imageBase64String.length * (3 / 4);
  }

  /**
   * Returns a base64 string based on an image that is less than 1mb in size,
   * to comply with the dexf 1mb limitation on images.
   */
  getCompressedSceneImage(startingX, startingY, floor, height) {
    let resolution = 1.1;
    let sceneImage = '';
    do {
      resolution -= 0.1;
      sceneImage = this.createImageStringFromTexture(
        startingX,
        startingY,
        floor,
        height,
        resolution
      );
    } while (this.getFileSizeOfBase64Image(sceneImage) > 1 * Math.pow(10, 6));
    return sceneImage;
  }

  getSceneImage(limitResolution) {
    this.wallResizerActive &&
      store.dispatch(actionSetWallResizerInactive({ nonInteraction: true }));

    store.dispatch(actionSetProppingVisibility(false));
    this.measurementLayer.measurements.clear();

    const { room } = this;
    const { floor } = room;

    const startingY = room.y + room.wall.y;
    const height = room.height;
    const startingX = floor.x + room.x;

    if (limitResolution)
      return this.getCompressedSceneImage(startingX, startingY, floor, height);

    return this.createImageStringFromTexture(
      startingX,
      startingY,
      floor,
      height,
      this.renderer.resolution
    );
  }

  resizeScene({ viewRect, trashcanRect }) {
    const currentSceneRect = selectSceneRect(store.getState());
    if (this.renderer) {
      this.trashcanRect = trashcanRect;

      this.renderer.resize(viewRect.width, viewRect.height);
      this.measurementLayer.renderer.resize(viewRect.width, viewRect.height);
      this.trashcanBelow =
        trashcanRect.y > currentSceneRect.y + currentSceneRect.height;
      this.update(true);
    }
  }

  showToasts(tac) {
    const { toastFlags } = tac;
    if (!toastFlags) {
      return;
    }

    toastFlags.forEach(key => {
      showTacToast(key);
    });

    store.dispatch(actions.removeToastFlags());
  }

  update(force = false) {
    if (!this.renderer) {
      // sometimes the applications is destroyed before the redux subscription triggers an
      // update, so we need to ensure the renderer is not nul before try update the scene
      return;
    }

    const state = store.getState();
    const { userAgent, scene, popups } = state;
    const tac = state.tac.present;

    const options = {
      isMobile: userAgent.isMobile,
      isPortrait: userAgent.isPortrait,
      measurementsActive: scene.measurementsActive,
      wallResizerActive: scene.wallResizerActive,
      errorVisible: popups.sceneErrorVisible && !scene.wallResizerActive,
      introPopupsVisible: popups.introPopupsVisible,
      hasShownPegboardHint: popups.hasShownPegboardHint,
      popups: popups.scenePopups,
      firstRender: !this.hasRendered,
      showPropping: scene.showPropping,
    };

    if (force || tac !== this.tac || !deepEqual(options, this.options)) {
      // Scene has changed so cached image is no longer valid
      storage.session.removeItem(
        constants.SESSION_STORAGE.CHECKOUT_IMAGE_CACHE_KEY
      );

      const instantErrors =
        tac?.errors?.filter(error => error.options?.showInstantly) || [];
      if (instantErrors.length && !popups.sceneErrorVisible) {
        store.dispatch(actionShowSceneErrors(instantErrors));
      } else if (!instantErrors.length && popups.sceneErrorVisible) {
        store.dispatch(actionHideSceneErrors());
      }

      tac && this.showToasts(tac);

      const leftDepth =
        Math.cos(constants.OBLIQUE_ANGLE) * constants.ROOM_DEPTH * 0.29;

      if (
        !(this.wallResizerActive && scene.wallResizerActive) ||
        !this.canvasMargins
      ) {
        this.canvasMargins = selectSceneMargins(store.getState());
      }

      this.wallResizerActive = scene.wallResizerActive;
      this.measurementsActive = scene.measurementsActive;

      this.room.update();

      this.itemContainer.x = this.hintContainer.x = isFixedRoom()
        ? this.room.x + this.room.wall.x
        : this.room.x + leftDepth * this.room.ratio;
      this.measurementLayer.setX(this.itemContainer.x);

      this.itemContainer.y = this.hintContainer.y = isFixedRoom()
        ? this.room.y +
          this.room.wall.y +
          this.canvasMargins.top * this.room.ratio
        : this.room.y + this.canvasMargins.top;
      this.measurementLayer.setY(this.itemContainer.y);

      this.tac = tac;
      this.options = options;

      if (this.tac) {
        this.itemContainer.update({
          tac,
          options,
          room: this.room.model,
          ratio: this.room.ratio,
          wallResizerActive: options.wallResizerActive,
        });

        this.measurementLayer.update({
          origo: this.itemContainer.origo,
          active: options.measurementsActive,
          wallResizerActive: options.wallResizerActive,
          tac,
          room: this.room.model,
          ratio: this.room.ratio,
          isMobile: options.isMobile,
          isPortrait: options.isPortrait,
        });

        this.adaptProppings();

        this.hasRendered = true;
        // tell extendable items about it
        emitter.emit(SCENE_REDRAWN, tac);
      }

      this.render();
    }
  }

  adaptProppings(movingItem, sprite) {
    const { dragging } = this;
    const movingParent = sprite?.parentItem || { x: 0, y: 0, z: 0 };
    const gMovingItem = movingItem
      ? {
          ...movingItem,
          x: movingParent.x,
          y: movingParent.y,
          z: movingParent.z,
        }
      : movingItem;
    const itemsToAdapt = tacHelpers.getProppingItemsToAdapt(
      this.tac,
      gMovingItem,
      dragging && dragging.item,
      dragging && dragging.originalItem
    );

    itemsToAdapt.forEach(item => {
      const sprite = this.itemContainer.getSprite(item.itemid);
      sprite &&
        sprite.adaptPropping &&
        sprite.adaptPropping(movingItem, dragging?.sprite);
    });
  }

  withoutClothesRails(item, filter) {
    const crids = tacHelpers
      .getClothesRails(item)
      .filter(productsService.isMultiParentProduct)
      .filter(cr => cr.logic && cr.logic.extendable)
      .map(item => item.itemid);
    const removeCrids = crids
      .map(crid => this.itemContainer.getSprite(crid))
      .filter(filter)
      .map(cr => cr.item.itemid);
    return {
      item: tacHelpers.filterTac(item, removeCrids),
      removed: removeCrids,
    };
  }

  crFilters = {
    hidden: cr => cr.adjustableContainer && cr.adjustableContainer.alpha === 0,
    all: () => true,
  };

  isInTrashcan(e) {
    const mouse = e && e.data && e.data.global;
    if (!mouse) {
      return false;
    }
    const trash = this.trashcanRect;

    return (
      mouse.x > trash.x &&
      mouse.x < trash.x + trash.width &&
      mouse.y > trash.y &&
      mouse.y < trash.y + trash.height
    );
  }

  isInScene(e) {
    const { offset, sprite } = this.dragging;

    const mouse = e && e.data && e.data.global;

    if (!mouse) {
      return false;
    }

    const roomBounds = this.room.getBounds();

    if (this.trashcanBelow) {
      const spriteVerticalLimit =
        this.itemContainer.y +
        (mouse.y - offset.y) +
        sprite.getBounds().height / 4;

      return spriteVerticalLimit < roomBounds.y + roomBounds.height;
    } else {
      const spriteHorizontalLimit =
        this.itemContainer.x +
        (mouse.x - offset.x) +
        sprite.getBounds().width / 4;

      return spriteHorizontalLimit < roomBounds.x + roomBounds.width;
    }
  }
  getItemBounds(item) {
    return this.itemContainer.getItemBounds(item);
  }

  getSprite(itemid) {
    if (itemid.id && productsService.isSection(itemid.id)) {
      // this is an item, not an itemid
      const item = itemid;
      const tacItem = tacHelpers.getItem(this.tac, item.itemid);
      if (tacItem) {
        return this.itemContainer.getSprite(item.itemid);
      }
      // not in tac so probably a fake item, let's try and find it
      const actualItem = this.tac.items.find(
        cand => cand.id === item.id && cand.x === item.x && cand.y === item.y
      );
      if (actualItem) {
        return this.itemContainer.getSprite(actualItem.itemid);
      }
    }
    return this.itemContainer.getSprite(itemid);
  }

  removeItem(item, reason) {
    store.dispatch(thunkRemoveItem(item, undefined, reason));
  }

  updateItem(item, parent, meta) {
    store.dispatch(thunkUpdateItem(item, parent, meta));
  }

  __debug__drawRects(rects, color, options) {
    if (options && options.clear) {
      this.rectContainer.removeChildren();
    }
    return rects.map(irect => {
      const rect = { ...irect };

      rect.y =
        this.room.height -
        (rect.y + this.itemContainer.y + rect.height) * this.room.ratio -
        this.room.y +
        this.itemContainer.y -
        (this.room.height - this.room.skirt.y) +
        33;
      rect.x =
        (rect.x -
          this.itemContainer.origo.x -
          this.room.x +
          this.itemContainer.x +
          500) *
        this.room.ratio;

      const sprite = new Rect({
        item: rect,
        ratio: this.room.ratio,
      });
      this.rectContainer.addChild(sprite);
      sprite.drawOutline(color);
      return sprite;
    });
  }

  __debug__drawPoly(poly, color, options) {
    if (options && options.clear) {
      this.rectContainer.removeChildren();
    }

    const localy = y => {
      return (
        this.room.height -
        (y + this.itemContainer.y) * this.room.ratio -
        this.room.y +
        this.itemContainer.y -
        (this.room.height - this.room.skirt.y) +
        33
      );
    };

    const localx = x => {
      return (
        (x -
          this.itemContainer.origo.x -
          this.room.x +
          this.itemContainer.x +
          500) *
        this.room.ratio
      );
    };

    const canvas = new PIXI.Graphics();

    canvas.lineStyle(2, color);

    poly.regions.forEach(region => {
      const points = region.map(coords => [
        localx(coords[0]),
        localy(coords[1]),
      ]);
      canvas.drawPolygon(new PIXI.Polygon(points.flat()));
    });
    this.rectContainer.addChild(canvas);
  }
}
